/**
 * FinalizeStep — pick which of the finalized designs go into the report, then
 * buy one. Paid tiers open their Stripe checkout (the report is delivered after
 * payment); the referral tier emails info@tessa.ai. A preview download is wired
 * for the selected designs while payment-gating lives on the backend.
 */
import { useState } from "react";
import { observer } from "mobx-react-lite";
import type { GeneratedItem } from "src/types/GeneratedItem";
import { useFeatureFlag } from "src/contexts/posthog.context";
import { FLAGS } from "src/posthog/flags";
import { useActiveParams } from "src/hooks/useActiveParams";
import { Icon, ACCENT } from "./Icon";
import { DesignThumb } from "./DesignThumb";
import { deriveRows } from "./ExploreView";
import { REPORT_CATALOG } from "./reportCatalog";
import type { BoardState } from "./BoardState";
import type { BriefSectionState } from "./briefModel";

const int = (n: number) => Math.round(n).toLocaleString();
const eurM = (n: number) => "€" + (n / 1e6).toFixed(1) + "M";
const GREEN = "#16a34a";

// Tag a Stripe payment link with client_reference_id so the completed payment
// can be mapped back to its project + report type when Stripe redirects the
// user home (verified via /api/stripe/checkout-session-status). Format is
// "<projectId>:<reportType>".
const withRef = (url: string, projectId: string | undefined, reportType: string): string => {
  if (!projectId) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("client_reference_id", `${projectId}:${reportType}`);
    return u.toString();
  } catch {
    return url;
  }
};

const icons = {
  back: <path d="M15 6l-6 6 6 6" />,
  check: <path d="M5 13l4 4 10-10" />,
  doc: (
    <g>
      <path d="M5 3h8l5 5v13H5z" />
      <path d="M13 3v5h5" />
    </g>
  ),
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  download: <path d="M12 16V4M6 10l6 6 6-6M4 20h16" />,
  mail: (
    <g>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M4 7l8 6 8-6" />
    </g>
  ),
};

export const FinalizeStep = observer(function FinalizeStep({
  board,
  sections,
  projectName,
  onBack,
  onSetExportView,
}: {
  board: BoardState;
  sections: BriefSectionState[];
  projectName: string;
  onBack: () => void;
  onSetExportView: (items: GeneratedItem[], reportType: "construction" | "financial") => void;
}) {
  const designs = sections.flatMap((s) => deriveRows(board.shownOf(s.id)));
  const [selected, setSelected] = useState<Set<string>>(() => new Set(designs.map((d) => d.key)));
  const chosen = designs.filter((d) => selected.has(d.key));
  const showTiers = useFeatureFlag(FLAGS.reportPaymentLink);
  const showPreview = useFeatureFlag(FLAGS.reportPreview);
  const { projectId } = useActiveParams();

  const toggle = (key: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) {
        if (n.size <= 1) return s; // keep at least one in the report
        n.delete(key);
      } else n.add(key);
      return n;
    });

  return (
    <div className="h-full overflow-y-auto bg-[#f9fafb]">
      <div className="max-w-[900px] mx-auto px-6 py-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-[12px] text-[#6b7280] hover:text-[#374151] mb-4"
        >
          <Icon d={icons.back} size={15} /> Back to Explore
        </button>

        <h1 className="text-[24px] font-semibold text-[#111827]">Finalize your designs</h1>
        <p className="text-[12px] text-[#6b7280] mt-1 mb-5">
          {projectName} · pick which designs go into your report, then choose a report below.
        </p>

        {/* design selection */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          {designs.map((r) => {
            const on = selected.has(r.key);
            return (
              <button
                key={r.key}
                onClick={() => toggle(r.key)}
                className={
                  "text-left rounded-lg border bg-white p-3 transition-colors " +
                  (on ? "border-[#86efac] ring-1 ring-[#86efac]" : "border-[#e5e7eb] opacity-60")
                }
              >
                <div className="flex items-center gap-2 mb-2">
                  <DesignThumb
                    item={r.item}
                    className="w-9 h-9 object-cover rounded-md bg-white border border-[#e5e7eb]"
                  />
                  <span className="text-[13px] font-semibold text-[#1f2937] grow truncate">
                    {r.name}
                  </span>
                  <span
                    className="w-5 h-5 grid place-items-center rounded-full border"
                    style={
                      on
                        ? { background: GREEN, borderColor: GREEN, color: "#fff" }
                        : { borderColor: "#d1d5db", color: "transparent" }
                    }
                  >
                    <Icon d={icons.check} size={12} />
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-center">
                  {[
                    ["Units", int(r.units)],
                    ["Cost", eurM(r.cost)],
                    ["CO₂", int(r.co2) + "t"],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="text-[12px] tabular-nums text-[#374151]">{v}</div>
                      <div className="text-[9px] uppercase tracking-wide text-[#9ca3af]">{k}</div>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-[#6b7280] mb-8">
          <span className="font-semibold text-[#374151]">{chosen.length}</span> of {designs.length}{" "}
          design{designs.length === 1 ? "" : "s"} in your report.
        </p>

        {/* report catalog — real Stripe payment links (report-payment-link) */}
        {showTiers && (
          <>
            <h2 className="text-[15px] font-semibold text-[#1f2937] mb-1">Get a report</h2>
            <p className="text-[11px] text-[#9ca3af] mb-3">
              Covers your {chosen.length} selected design{chosen.length === 1 ? "" : "s"} ·
              delivered once payment completes.
            </p>
            <div className="grid grid-cols-3 gap-3">
              {REPORT_CATALOG.map((rt) => (
                <div
                  key={rt.id}
                  className="rounded-xl border border-[#e5e7eb] bg-white p-4 flex flex-col"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1f2937]">
                      <span style={{ color: ACCENT }}>
                        <Icon d={rt.contact ? icons.mail : icons.doc} size={15} />
                      </span>
                      {rt.name}
                    </span>
                    <span
                      className={
                        "tabular-nums " +
                        (rt.contact
                          ? "text-[11px] text-[#9ca3af]"
                          : "text-[13px] font-semibold text-[#111827]")
                      }
                    >
                      {rt.price}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#9ca3af] leading-snug grow mb-3">{rt.desc}</p>
                  <a
                    href={rt.contact ? rt.url : withRef(rt.url, projectId, rt.id)}
                    target={rt.contact ? undefined : "_blank"}
                    rel="noreferrer"
                    className={
                      "inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[12px] font-semibold hover:opacity-90 transition-opacity " +
                      (rt.contact ? "border border-[#d1d5db] text-[#4b5563]" : "text-white")
                    }
                    style={rt.contact ? undefined : { background: ACCENT }}
                  >
                    {rt.cta} <Icon d={rt.contact ? icons.mail : icons.arrow} size={15} />
                  </a>
                </div>
              ))}
            </div>
          </>
        )}

        {/* preview — set the export view, then download (report-preview) */}
        {showPreview && (
          <>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                onClick={() =>
                  onSetExportView(
                    chosen.map((d) => d.item),
                    "construction"
                  )
                }
                className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg border border-[#d1d5db] text-[12px] font-medium text-[#4b5563] hover:bg-white"
              >
                <Icon d={icons.download} size={14} /> Construction report ({chosen.length})
              </button>
              <button
                onClick={() =>
                  onSetExportView(
                    chosen.map((d) => d.item),
                    "financial"
                  )
                }
                className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg border border-[#d1d5db] text-[12px] font-medium text-[#4b5563] hover:bg-white"
              >
                <Icon d={icons.download} size={14} /> Financial feasibility ({chosen.length})
              </button>
            </div>
            <p className="text-[10px] text-[#9ca3af] mt-3 text-center">
              Preview download (no payment) · set the export view, then download. Financial adds a
              cost page per design.
            </p>
          </>
        )}

        {!showTiers && !showPreview && (
          <p className="text-[12px] text-[#9ca3af] text-center mt-6">
            Reports aren&apos;t enabled for your account yet.
          </p>
        )}
      </div>
    </div>
  );
});
